import type { RepoRole } from '../../utils/repo-role.util';
import { inferRepoRoleFromRepoRef } from '../../utils/repo-role.util';
export { compactChatMessages } from '../../models/orchestrator-surface.model';
export type { ChatMessage } from '../../models/orchestrator-surface.model';
export type { RepoRole };
export { inferRepoRoleFromRepoRef };

export type GitHubRepoRef = { owner: string; name: string; role?: RepoRole };

export type ReposSettings = {
  github: {
    installationId: number | null;
    // Project-scoped repo set (multi-repo).
    repos: GitHubRepoRef[];
  };
};

export type ReposSettingsPatch = {
  github?: Partial<ReposSettings['github']>;
};

export type RepoOverviewState = {
  // Lightweight cache to avoid empty UI while loading panels.
  repo: {
    fullName?: string;
    defaultBranch?: string;
    private?: boolean;
    description?: string;
    url?: string;
  } | null;
  pulls: any[];
  commits: any[];
  status: any | null;
  branches?: any[];
  repos: Record<
    string,
    {
      repo: any | null;
      branches: any[];
      pulls: any[];
      commits: any[];
      status: any | null;
      lastRefreshedAt: number | null;
    }
  >;
  lastRefreshedAt: number | null;
};

export function compactReposState(state: RepoOverviewState): RepoOverviewState {
  const compactEntry = (v: any) => ({
    repo: v?.repo ?? null,
    branches: Array.isArray(v?.branches) ? v.branches.slice(0, 25) : [],
    pulls: Array.isArray(v?.pulls) ? v.pulls.slice(0, 20) : [],
    commits: Array.isArray(v?.commits) ? v.commits.slice(0, 20) : [],
    status: v?.status ?? null,
    lastRefreshedAt: typeof v?.lastRefreshedAt === 'number' ? v.lastRefreshedAt : null,
  });
  const repos = Object.entries(state?.repos ?? {}).reduce((acc: any, [k, v]) => {
    acc[k] = compactEntry(v);
    return acc;
  }, {} as Record<string, any>);
  return {
    repo: state?.repo ?? null,
    branches: Array.isArray(state?.branches) ? state.branches.slice(0, 25) : [],
    pulls: Array.isArray(state?.pulls) ? state.pulls.slice(0, 20) : [],
    commits: Array.isArray(state?.commits) ? state.commits.slice(0, 20) : [],
    status: state?.status ?? null,
    repos,
    lastRefreshedAt: typeof state?.lastRefreshedAt === 'number' ? state.lastRefreshedAt : null,
  };
}

export function normalizeReposSettings(raw: any): ReposSettings {
  const installationId =
    raw?.github?.installationId == null
      ? null
      : typeof raw.github.installationId === 'number'
      ? raw.github.installationId
      : Number(raw.github.installationId);

  const rawRepos = Array.isArray(raw?.github?.repos) ? raw.github.repos : [];
  const repos: GitHubRepoRef[] = rawRepos
    .map((r: any) => ({
      owner: typeof r?.owner === 'string' ? r.owner.trim() : '',
      name: typeof r?.name === 'string' ? r.name.trim() : '',
      role: inferRepoRoleFromRepoRef(r),
    }))
    .filter((r: any) => r.owner && r.name);

  return {
    github: {
      installationId: Number.isFinite(installationId) ? (installationId as number) : null,
      repos,
    },
  };
}

export function normalizeReposState(raw: any): RepoOverviewState {
  const pulls = Array.isArray(raw?.pulls) ? raw.pulls : [];
  const commits = Array.isArray(raw?.commits) ? raw.commits : [];
  const branches = Array.isArray(raw?.branches) ? raw.branches : [];
  const lastRefreshedAt = typeof raw?.lastRefreshedAt === 'number' ? raw.lastRefreshedAt : null;
  const repo = raw?.repo && typeof raw.repo === 'object' ? raw.repo : null;
  const normalizeEntry = (v: any) => ({
    repo: v?.repo && typeof v.repo === 'object' ? v.repo : null,
    branches: Array.isArray(v?.branches) ? v.branches : [],
    pulls: Array.isArray(v?.pulls) ? v.pulls : [],
    commits: Array.isArray(v?.commits) ? v.commits : [],
    status: v?.status ?? null,
    lastRefreshedAt: typeof v?.lastRefreshedAt === 'number' ? v.lastRefreshedAt : null,
  });
  const reposRaw = raw?.repos && typeof raw.repos === 'object' && !Array.isArray(raw.repos) ? raw.repos : {};
  const repos = Object.entries(reposRaw).reduce((acc: any, [k, v]) => {
    const key = String(k ?? '').trim();
    if (!key) return acc;
    acc[key] = normalizeEntry(v);
    return acc;
  }, {} as Record<string, any>);
  return {
    repo,
    branches,
    pulls,
    commits,
    status: raw?.status ?? null,
    repos,
    lastRefreshedAt,
  };
}
